import { Injectable } from '@angular/core';
import { InternalDatabaseService } from '../internal/internal-database.service';
import { TheatrePatientServices } from './theatre-patient-services';
import { Observable, combineLatest } from 'rxjs';
import { Service } from '@stockcare-storage/service/service';
import { IService } from '@stockcare-models/IService';
import { ITheatrePatientVisit } from '@stockcare-models/ITheatrePatientVisit';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { Settings } from '@stockcare-helpers/settings';
import { take } from 'rxjs/operators';
import { NonNullableFormBuilder } from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
export class TheatrePatientServicesStorageService {
  readonly table: string;
  readonly index: string;
  enableLocaldb: boolean;
  patientServices: TheatrePatientServices[] = [];

  constructor(private internalDatabaseService: InternalDatabaseService,
    private readonly settingsService: SettingsService) {
    const table = TheatrePatientServices.getTableDefinition();
    this.table = table.TableName;
    this.index = table.Indexes[0].Name;
  }

  addUpdateServices(theatrePatient: ITheatrePatientVisit): Observable<boolean> {
    this.getSettings();
    return new Observable((observer) => {
      const subscriptions: Array<Observable<boolean>> = [];
      if (theatrePatient.services && theatrePatient.services.length > 0) {
        theatrePatient.services.forEach((x) => {
          subscriptions.push(
            this.add(theatrePatient.id, this.map(theatrePatient.id, x))
          );
        });
      } else {
        observer.next();
      }

      combineLatest(subscriptions).subscribe((result) => {
        observer.next();
      });
    });
  }

  add(
    theatrePatientId: number,
    service: TheatrePatientServices
  ): Observable<boolean> {
    return new Observable((observer) => {
      const theatrePatientService = new TheatrePatientServices();
      theatrePatientService.TheatrePatientId = theatrePatientId;
      theatrePatientService.ServiceId = service.Id;
      theatrePatientService.Quantity = service.Quantity;
      theatrePatientService.OriginalQuantity = service.Quantity;
      theatrePatientService.Barcode = service.Barcode;
      theatrePatientService.UnitOfMeasure = service.UnitOfMeasure;
      theatrePatientService.Name = service.Name;
      theatrePatientService.InstitutionId = service.InstitutionId;
      theatrePatientService.PackId = service.PackId;
      theatrePatientService.PackBasketId = service.PackBasketId;
      theatrePatientService.Username = service.Username;
      if(this.enableLocaldb){
        this.internalDatabaseService
          .add(this.table, theatrePatientService)
          .then(() => {
            observer.next(true);
          })
          .catch((error) => {
            observer.error(false);
          });
      }
      else{
        theatrePatientService.Id = this.patientServices.length + 1;
        this.patientServices.push(theatrePatientService);
        observer.next(true);
      }
    });
  }

  addToBasket(
    theatrePatientId: number,
    service: TheatrePatientServices
  ): Observable<boolean> {
    return new Observable((observer) => {
      this.getAllByTheatrePatientId(theatrePatientId).subscribe(
        (theatrePatientServices) => {
          const existingServices = theatrePatientServices.filter(
            (x) => x.ServiceId === service.Id
          );

          if (existingServices && existingServices.length > 0) {
            const existingService = existingServices[0];
            existingService.Quantity++;
            if(this.enableLocaldb){
              this.internalDatabaseService
                .update(this.table, existingService)
                .then(() => {
                  observer.next(true);
                })
                .catch((error) => {
                  observer.error(false);
                });
            }
            else{
              observer.next(true);
            }
          } else {
            service.Quantity = 1;
            this.add(theatrePatientId, service).subscribe(
              () => {
                observer.next();
              },
              (error) => observer.error()
            );
          }
        }
      );
    });
  }

  removeByPatientId(theatrePatientId: number): Promise<void> {
    return new Promise((resolve)=> {
      this.getAllByTheatrePatientId(theatrePatientId)
      .subscribe((services)=>{
        if(services){
          services.forEach(service => {
            this.remove(service.Id);
          });
          
            resolve();
        }
        else{
          resolve();
        }
      });
    });
  }

  removeByPatientAndPackId(theatrePatientId: number, packId: number): Promise<void> {
    return new Promise((resolve)=> {
      this.getAllByTheatrePatientAndPackId(theatrePatientId, packId)
      .subscribe((services)=>{
        if(services){
          services.forEach(async (service)=>{
            this.patientServices = this.patientServices.filter((x)=> x.ServiceId != service.ServiceId && x.TheatrePatientId != theatrePatientId);
          });

            resolve();
        }
        else{
          resolve();
        }
      });
    });
  }

  remove(serviceId: number): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      if(this.enableLocaldb){
        this.internalDatabaseService
          .delete(this.table, serviceId)
          .then(() => {
            observer.next(true);
          })
          .catch((error) => {
            observer.error(false);
          });
      }
      else{
        this.patientServices = this.patientServices.filter((x)=> x.Id !== serviceId);
        observer.next(true);
      }
    });
  }

  async deleteAll() {
    if(this.enableLocaldb){
      await this.internalDatabaseService.deleteAll(this.table);
    }
    else{
      this.patientServices = [];
    }
  }

  async deleteByPatientTheatreVisitId(theatrePatientVisitId: number) {
    this.patientServices = this.patientServices.filter(x => x.TheatrePatientId != theatrePatientVisitId);
  }

  async delete(primaryKey: number) {
    if(this.enableLocaldb){
      return await this.internalDatabaseService.delete(this.table, primaryKey);
    }
    else{
      return new Promise<void>((resolve,reject)=>{ 
        this.patientServices = this.patientServices.filter((service)=> service.Id !== primaryKey);
        resolve();
      });
    }
  }

  getAllByTheatrePatientId(
    theatrePatientId: number
  ): Observable<TheatrePatientServices[]> {
    return new Observable((observer) => {
      if(this.enableLocaldb){
        this.internalDatabaseService
          .getAll<TheatrePatientServices>(this.table)
          .then((theatrePatientServices) => {
            const patients: TheatrePatientServices[] = [];

            theatrePatientServices.forEach((x) => {
              if (x.TheatrePatientId === theatrePatientId) {
                patients.push(x);
              }
            });

            observer.next(patients);
          })
          .catch((error) => {
            observer.error(null);
          });
        }
        else{
          const patients: TheatrePatientServices[] = [];
          this.patientServices.forEach((x) => {
            if (x.TheatrePatientId === theatrePatientId) {
              patients.push(x);
            }
          });
          observer.next(patients);
        }
    });
  }

  getAllByTheatrePatientAndPackId(
    theatrePatientId: number,
    packId: number
  ): Observable<TheatrePatientServices[]> {
    return new Observable((observer) => {
      if(this.enableLocaldb){
        this.internalDatabaseService
          .getAll<TheatrePatientServices>(this.table)
          .then((theatrePatientServices) => {
            const patients: TheatrePatientServices[] = [];

            theatrePatientServices.forEach((x) => {
              if (x.TheatrePatientId === theatrePatientId && x.PackId === packId) {
                patients.push(x);
              }
            });

            observer.next(patients);
          })
          .catch((error) => {
            observer.error(null);
          });
        }
        else{
          const patients: TheatrePatientServices[] = [];
          this.patientServices.forEach((x) => {
            if (x.TheatrePatientId === theatrePatientId && x.PackId === packId) {
              patients.push(x);
            }
          });
          observer.next(patients);
        }
    });
  }

  restoreFromBackup(
    theatrePatientId: number,
    servicesBackup: TheatrePatientServices[]
  ) {
    return new Observable((observer) => {
      const addSubscriptions = Array<Observable<boolean>>();
      if(this.enableLocaldb){
        this.getAllByTheatrePatientId(theatrePatientId).subscribe((services) => {
          if (services) {
            services.forEach(async (x) => await this.delete(x.Id));
          }
          if (servicesBackup && servicesBackup.length > 0) {
            servicesBackup.forEach((x) =>
              addSubscriptions.push(this.add(theatrePatientId, x))
            );
          } else {
            observer.next();
          }
        });
     }
     else{
       this.patientServices = this.patientServices.filter((service)=> service.TheatrePatientId !== theatrePatientId);
       if (servicesBackup && servicesBackup.length > 0) {
        servicesBackup.forEach((x) =>
          addSubscriptions.push(this.add(theatrePatientId, x))
        );
      } else {
        observer.next();
      }

      combineLatest([addSubscriptions]).pipe(take(1)).subscribe(() => {
        observer.next();
      });
     }
    });
  }

  map(theatrePatientId: number, item: IService): TheatrePatientServices {
    const service = new TheatrePatientServices();

    service.Id = item.id;
    service.Barcode = item.barcode;
    service.Name = item.name;
    service.Quantity = item.quantity;
    service.ServiceId = item.serviceId;
    service.UnitOfMeasure = item.unitOfMeasure;
    service.TheatrePatientId = theatrePatientId;
    service.InstitutionId = item.institutionId;
    service.PackBasketId = item.packBasketId;
    service.PackId = item.packId;
    service.Username = item.username;

    return service;
  }

  mapFromTheatrePatientService(item: TheatrePatientServices): IService {
    return {
      barcode: item.Barcode,
      id: item.Id,
      name: item.Name,
      quantity: item.Quantity,
      serviceId: item.ServiceId,
      unitOfMeasure: item.UnitOfMeasure,
      institutionId: item.InstitutionId,
      packId: item.PackId,
      packBasketId: item.PackBasketId,
      username: item.Username
    };
  }

  mapFromService(
    theatrePatientId: number,
    item: Service,
    packId?: number, 
    packBasketId?: string
  ): TheatrePatientServices {
    const service = new TheatrePatientServices();

    service.Id = item.Id;
    service.Barcode = item.Barcode;
    service.Name = item.Name;
    service.Quantity = item.Quantity;
    service.ServiceId = item.ServiceId;
    service.UnitOfMeasure = item.UnitOfMeasure;
    service.TheatrePatientId = theatrePatientId;
    service.InstitutionId = item.InstitutionId;
    
    if (packId) service.PackId = packId;
    if (packBasketId) service.PackBasketId = packBasketId;

    return service;
  }

  getSettings(){
    this.enableLocaldb = this.settingsService.getBooleanSetting(Settings.ENABLE_INDEXED_DB);
  }
}